import {D3ChartRenderable, D3ChartSize, D3GSelection, D3SvgSelection} from "../platform/d3chart/D3Chart";
import * as d3Scale from "d3-scale";

type FreqPoint = { rpm: number; freq: number };

function findCrossingIdx(rpms: number[], a: number[], b: number[]): number {
    for (let i = 0; i < rpms.length - 1; i++) {
        if ((a[i] - b[i]) * (a[i + 1] - b[i + 1]) <= 0) return i;
    }
    return -1;
}

function interpAt(rpms: number[], values: number[], idx: number, rpm: number): number {
    const t = (rpm - rpms[idx]) / (rpms[idx + 1] - rpms[idx]);
    return values[idx] + t * (values[idx + 1] - values[idx]);
}

function interpCrossing(rpms: number[], a: number[], b: number[], idx: number): FreqPoint {
    const da = a[idx] - b[idx];
    const da2 = a[idx + 1] - b[idx + 1];
    const t = da / (da - da2);
    return {
        rpm: rpms[idx] + t * (rpms[idx + 1] - rpms[idx]),
        freq: a[idx] + t * (a[idx + 1] - a[idx])
    };
}

function toSvgPath(scaleX: d3Scale.ScaleLinear<number, number, never>,
                   scaleY: d3Scale.ScaleLinear<number, number, never>,
                   points: FreqPoint[]): string {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(p.rpm)} ${scaleY(p.freq)}`).join(' ') + ' Z';
}

function redBandPoints(rpms: number[], lowerSafety: number[], upperSafety: number[], excitation: number[]): FreqPoint[] | null {
    const lowerCrossIdx = findCrossingIdx(rpms, excitation, lowerSafety);
    const upperCrossIdx = findCrossingIdx(rpms, excitation, upperSafety);
    if (lowerCrossIdx < 0 || upperCrossIdx < 0) return null;

    const lowerCross = interpCrossing(rpms, excitation, lowerSafety, lowerCrossIdx);
    const upperCross = interpCrossing(rpms, excitation, upperSafety, upperCrossIdx);
    const upperAtLowerCross = interpAt(rpms, upperSafety, lowerCrossIdx, lowerCross.rpm);
    const lowerAtUpperCross = interpAt(rpms, lowerSafety, upperCrossIdx, upperCross.rpm);

    const bottomEdge: FreqPoint[] = [lowerCross];
    for (let i = lowerCrossIdx + 1; i <= upperCrossIdx; i++) {
        bottomEdge.push({rpm: rpms[i], freq: lowerSafety[i]});
    }
    bottomEdge.push({rpm: upperCross.rpm, freq: lowerAtUpperCross});

    const topEdge: FreqPoint[] = [upperCross];
    for (let i = upperCrossIdx; i >= lowerCrossIdx + 1; i--) {
        topEdge.push({rpm: rpms[i], freq: upperSafety[i]});
    }
    topEdge.push({rpm: lowerCross.rpm, freq: upperAtLowerCross});

    return [...bottomEdge, ...topEdge];
}

export class CampbellSafetyBandsRenderable implements D3ChartRenderable {

    private g: D3GSelection | undefined;
    private rendered = false;

    constructor(private scaleX: d3Scale.ScaleLinear<number, number, never>,
                private scaleY: d3Scale.ScaleLinear<number, number, never>,
                private rpms: number[],
                private lowerSafety: number[],
                private upperSafety: number[],
                private excitationCurves: number[][]) {
    }

    initialize(_svg: D3SvgSelection, rootG: D3GSelection, _unclipped: D3GSelection, _size: D3ChartSize): void {
        this.g = rootG.append("g");
        this.g.append("path").attr("class", "safety-band-green");
        this.excitationCurves.forEach((_, i) =>
            this.g!.append("path").attr("class", `safety-band-red-${i}`)
        );
    }

    render(): void {
        if (this.rendered || !this.g) return;

        this.g.select(".safety-band-green")
            .attr("fill", "rgba(0, 180, 0, 0.15)")
            .attr("stroke", "none")
            .attr("d", toSvgPath(this.scaleX, this.scaleY, this.fullGreenPoints()));

        this.excitationCurves.forEach((curve, i) => {
            const pts = redBandPoints(this.rpms, this.lowerSafety, this.upperSafety, curve);
            if (!pts) return;
            this.g!.select(`.safety-band-red-${i}`)
                .attr("fill", "rgba(200, 0, 0, 0.25)")
                .attr("stroke", "none")
                .attr("d", toSvgPath(this.scaleX, this.scaleY, pts));
        });

        this.rendered = true;
    }

    private fullGreenPoints(): FreqPoint[] {
        const bottom: FreqPoint[] = this.rpms.map((rpm, i) => ({rpm, freq: this.lowerSafety[i]}));
        const top: FreqPoint[] = this.rpms.map((rpm, i) => ({rpm, freq: this.upperSafety[i]})).reverse();
        return [...bottom, ...top];
    }
}
